import { Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, takeUntil, tap, throwError } from 'rxjs';
import {
  AccountDetails,
  CreateCustomerResponse,
  CustomerDetails,
  CustomerProfile,
  NotificationPreferences,
  PaymentMethod,
  ProfileSetupPayload,
  SubscriptionDetails,
  UpdateCustomerDetailsPayload,
  UpdateCustomerSubscription,
} from '../models/customer.model';
import { AuthService } from './auth.service';
import { AppStorageService } from './app-storage.service';
import { CUSTOMER } from '../constants';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { PaymentIntent, SetupIntent } from '../models/common.model';
import { SubscriptionType } from '../enums/common.enums';

@Injectable({ providedIn: 'root' })
export class CustomerService {
  constructor(
    private authService: AuthService,
    private appStorageService: AppStorageService,
    private http: HttpClient
  ) {
    this.authService.logout$.subscribe(() => this._accountDetails.set(null));
  }

  private mapCustomerDetailsToProfile(
    details: CustomerDetails
  ): Partial<CustomerProfile> {
    return {
      ppuId: details.ppuId,
      fullName: `${details.firstName} ${details.lastName}`,
      subscriptionType: details.subscriptionType,
      scheduledCancellationDate: details.scheduledCancellationDate,
      address: {
        addressLine1: details.addressLine1,
        addressLine2: details.addressLine2,
        city: details.city,
        provinceCode: details.provinceCode,
        postalCode: details.postalCode,
      },
    };
  }

  getCustomerProfile(forceRefresh = false): Observable<CustomerProfile | null> {
    const account = this.authService.getAccount();

    const emailAddress =
      (account?.idTokenClaims?.['emailAddress'] as string) ??
      (account?.idTokenClaims?.['preferred_username'] as string) ??
      account?.username ??
      null;

    const userId = (account?.idTokenClaims?.['userId'] as string) ?? null;

    const ppuRole = (account?.idTokenClaims?.['ppuRole'] as string) ?? null;

    if (ppuRole === CUSTOMER && !!userId) {
      this.appStorageService.customerEmailAddress.set(emailAddress);
      return this.getAccountDetails(forceRefresh).pipe(
        map((accountDetails: AccountDetails) => {
          const profile: CustomerProfile = {
            emailAddress: emailAddress ?? '',
            userId: userId,
            ...this.mapCustomerDetailsToProfile(accountDetails.customerDetails),
          };
          this.appStorageService.setCustomerProfile(profile);
          return profile;
        }),
        catchError((error) => {
          // If API call fails, throw the error to be handled by the caller
          return throwError(() => error);
        })
      );
    } else {
      return of(null);
    }
  }

  /** Best-effort sync of the signed-in customer's details to their auth provider profile. */
  syncAuthProfile(): Observable<void> {
    return this.http
      .post<void>(
        `${environment.API_ROOT}api/v1/Customers/SyncAuthProfile`,
        {
          isGoogleSso: this.appStorageService.isGoogleSso(),
          isAppleSso: this.appStorageService.isAppleSso(),
        }
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  saveCustomerProfile(payload: ProfileSetupPayload): Observable<boolean> {
    return this.http
      .post<CreateCustomerResponse>(
        `${environment.API_ROOT}api/v1/Customers`,
        payload
      )
      .pipe(
        map((res: CreateCustomerResponse) => {
          this.appStorageService.setCustomerProfile({
            fullName: `${payload.firstName} ${payload.lastName}`,
            emailAddress: payload.emailAddress,
            userId: res.customerId,
            ppuId: res.ppuid,
          });
          return !!res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  createSubscriptionIntent(
    customerEmailAddress: string,
    spamToken?: string,
    postalCode?: string,
    customerName?: string
  ): Observable<PaymentIntent | null> {
    return this.http
      .post<PaymentIntent>(
        `${environment.API_ROOT}api/v1/Customers/CreateSubscriptionIntent`,
        {
          email: customerEmailAddress,
          spamPreventionToken: spamToken,
          postalCode: postalCode,
          customerName: customerName,
        }
      )
      .pipe(
        map((res: PaymentIntent) => {
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  /** Transient cache of the last `GetCustomerAccountDetails` response — Profile and
   * Notifications both need it, and it changes rarely enough that re-fetching on every
   * tap is wasted work. Logout clears it; `forceRefresh` and successful writes keep it up to date. */
  private _accountDetails = signal<AccountDetails | null>(null);

  private patchAccountDetailsCache(partial: Partial<AccountDetails>): void {
    const current = this._accountDetails();
    if (!current) return;
    this._accountDetails.set({
      ...current,
      ...partial,
      customerDetails: {
        ...current.customerDetails,
        ...partial.customerDetails,
      },
    });
  }

  getAccountDetails(forceRefresh = false): Observable<AccountDetails> {
    const cached = this._accountDetails();
    if (cached && !forceRefresh) {
      return of(cached);
    }

    return this.http
      .get<AccountDetails>(
        `${environment.API_ROOT}api/v1/Customers/GetCustomerAccountDetails`
      )
      .pipe(
        // Stop an old account response from repopulating the cache after logout.
        takeUntil(this.authService.logout$),
        tap((res: AccountDetails) => {
          this._accountDetails.set(res);
          this.appStorageService.updateCustomerProfile(
            this.mapCustomerDetailsToProfile(res.customerDetails)
          );
        }),
        catchError((error) => throwError(() => error))
      );
  }

  getSubscriptionDetails(): Observable<SubscriptionDetails> {
    return this.http
      .get<SubscriptionDetails>(
        `${environment.API_ROOT}api/v1/Customers/GetCustomerSubscriptionDetails`
      )
      .pipe(
        tap((res: SubscriptionDetails) => {
          this.appStorageService.updateCustomerProfile({
            subscriptionType: res.subscriptionType,
            scheduledCancellationDate: res.scheduledCancellationDate,
          });
        }),
        catchError((error) => throwError(() => error))
      );
  }

  updateCustomerDetails(
    payload: UpdateCustomerDetailsPayload
  ): Observable<CustomerDetails> {
    return this.http
      .put<CustomerDetails>(
        `${environment.API_ROOT}api/v1/Customers/UpdateCustomerDetails`,
        payload
      )
      .pipe(
        map((res: CustomerDetails) => {
          this.appStorageService.updateCustomerProfile(
            this.mapCustomerDetailsToProfile(res)
          );
          this.patchAccountDetailsCache({ customerDetails: res });
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  updateNotificationPreferences(
    payload: NotificationPreferences
  ): Observable<NotificationPreferences> {
    return this.http
      .put<NotificationPreferences | null>(
        `${environment.API_ROOT}api/v1/Customers/UpdateNotificationPreferences`,
        payload
      )
      .pipe(
        map((res: NotificationPreferences | null) => {
          const preferences = res ?? payload;
          this.patchAccountDetailsCache({
            notificationPreferences: preferences,
          });
          return preferences;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  setDefaultPaymentMethod(
    paymentMethodId: string,
    spamToken: string
  ): Observable<boolean> {
    return this.http
      .patch<boolean>(
        `${environment.API_ROOT}api/v1/Customers/UpdateDefaultPaymentMethod`,
        { paymentMethodId },
        { headers: { 'X-Spam-Prevention-Token': spamToken } }
      )
      .pipe(
        map((res: boolean) => res),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  updateSubscription(
    subscriptionType: SubscriptionType,
    spamToken: string,
    paymentIntentId?: string | undefined
  ): Observable<UpdateCustomerSubscription> {
    return this.http
      .patch<UpdateCustomerSubscription>(
        `${environment.API_ROOT}api/v1/Customers/UpdateCustomerSubscription`,
        { subscriptionType, paymentIntentId },
        { headers: { 'X-Spam-Prevention-Token': spamToken } }
      )
      .pipe(
        map((res: UpdateCustomerSubscription) => {
          this.appStorageService.updateCustomerProfile({
            subscriptionType: res.subscriptionType,
            scheduledCancellationDate: res.scheduledCancellationDate,
          });
          this.patchAccountDetailsCache({
            customerDetails: {
              ...this._accountDetails()?.customerDetails,
              subscriptionType: res.subscriptionType,
              scheduledCancellationDate: res.scheduledCancellationDate,
            } as CustomerDetails,
          });
          return res;
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  deletePaymentMethod(
    paymentMethodId: string,
    spamToken: string
  ): Observable<boolean> {
    return this.http
      .delete<boolean>(
        `${environment.API_ROOT}api/v1/Customers/DeletePaymentMethod`,
        {
          body: { paymentMethodId },
          headers: { 'X-Spam-Prevention-Token': spamToken },
        }
      )
      .pipe(
        map((res: boolean) => res),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  createPaymentMethodIntent(spamToken: string): Observable<SetupIntent> {
    return this.http
      .post<SetupIntent>(
        `${environment.API_ROOT}api/v1/Customers/CreatePaymentMethodIntent`,
        {},
        { headers: { 'X-Spam-Prevention-Token': spamToken } }
      )
      .pipe(
        map((res: SetupIntent) => res),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  confirmAddPaymentMethod(
    setupIntentId: string,
    spamToken: string
  ): Observable<PaymentMethod[]> {
    return this.http
      .post<PaymentMethod[]>(
        `${environment.API_ROOT}api/v1/Customers/ConfirmPaymentMethod`,
        { setupIntentId },
        { headers: { 'X-Spam-Prevention-Token': spamToken } }
      )
      .pipe(
        map((res: PaymentMethod[]) => res),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  deleteAccount(): Observable<boolean> {
    return this.http
      .delete<boolean>(
        `${environment.API_ROOT}api/v1/Customers/DeleteAccount`
      )
      .pipe(
        map(() => true),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }
}
